import React from 'react';

import BreadCrumb, { BreadCrumbOption } from 'components/BreadCrumb';
import Footer from 'components/Footer';
import Header, { ComponentProps as HeaderProps } from 'components/Header';
import Spinner from 'components/Icons/Spinner';
import Layout from 'components/Layout';
import Title from 'components/Title';
import VisuallyHidden from 'components/VisuallyHidden';

import * as Styled from './styled';

export type ComponentProps = {
  /**
   * Page title
   */
  title: string;
  /**
   * is the page in loading state
   */
  loading?: boolean;
  /**
   * Optional loading content to render as part of the page loading state
   */
  loadingContent?: JSX.Element;
  /**
   * Error component to render if was provided
   */
  error?: JSX.Element;
  /**
   * show/hide the element in the page
   */
  showFooter?: boolean;
  /**
   * the number of px padding inside of the content wrapper. defaults to 32px.
   */
  contentPadding?: number;
  /**
   * Optional content to render always regardless of when the page is lazy loaded or not
   */
  StaticContent?: JSX.Element;
  /**
   * Optional floating number between 0-1 to opacity the content between loading of the page "children" collection (defaults to '0')
   */
  opacityBetweenLoading?: number;
  /**
   * Optional transaction header image url
   */
  imgUrl?: string;
  /**
   * Optional bread crumbs
   */
  breadCrumbs?: BreadCrumbOption[];
  /**
   * Optional login button props in the header. Defaults to 'undefined'
   */
  headerProps?: HeaderProps;
  /**
   * Optional, background color of the main section. Defaults to white
   */
  backgroundColor?: string;
  /**
   * Optional, primary background color of banner
   */
  backgroundColorBannerPrimary?: string;
  /**
   * Optional, secondary background color of banner
   */
  backgroundColorBannerSecondary?: string;
};

export const Page: React.FC<ComponentProps> = ({
  title,
  loading = false,
  showFooter = false,
  contentPadding = 32,
  imgUrl,
  breadCrumbs = [],
  loadingContent,
  StaticContent,
  opacityBetweenLoading = 0,
  error,
  headerProps = {},
  backgroundColor = '',
  backgroundColorBannerPrimary = '',
  backgroundColorBannerSecondary = '',
  children
}) => {
  const Fallback = (
    <Styled.Content role='main' showLoading>
      <Styled.LoadingSpinner showLoading>
        <Spinner />
        {loadingContent}
      </Styled.LoadingSpinner>
    </Styled.Content>
  );

  return (
    <Styled.Wrapper>
      <VisuallyHidden>
        <a href='#content'>Jump to main content</a>
      </VisuallyHidden>
      <Header {...headerProps} />
      <BreadCrumb items={breadCrumbs} />
      <a id='content' />
      <Title
        text={title}
        imgUrl={imgUrl}
        backgroundColorPrimary={backgroundColorBannerPrimary}
        backgroundColorSecondary={backgroundColorBannerSecondary}
      />
      <Layout
        contentPadding={contentPadding}
        showLoading={loading}
        showFooter={showFooter}
        backgroundColor={backgroundColor}>
        {error}
        {StaticContent}
        <React.Suspense fallback={Fallback}>
          <Styled.Content role='main' showLoading={loading}>
            <Styled.LoadingSpinner showLoading={loading}>
              <Spinner />
              {loadingContent}
            </Styled.LoadingSpinner>
            <Styled.ChildrenWrapper
              opacity={opacityBetweenLoading}
              hide={loading || error}
              showLoading={loading}>
              {children}
            </Styled.ChildrenWrapper>
          </Styled.Content>
        </React.Suspense>
      </Layout>
      {<Footer showFooter={showFooter} />}
    </Styled.Wrapper>
  );
};

export default Page;
